import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Tent, GraduationCap, Users, Settings } from "lucide-react";
import CampRegistrationsTab from "./CampRegistrationsTab";
import CampManagementTab from "./CampManagementTab";

/**
 * Modular Registrations Section for Admin Dashboard
 * Contains sub-tabs for different registration types:
 * - Camp-Anmeldungen (fully implemented)
 * - Camp-Verwaltung (status & capacity management)
 * - Akademie-Anmeldungen (placeholder for future)
 */
export default function RegistrationsSection({ token }) {
  const [activeSubTab, setActiveSubTab] = useState("camps");

  const subTabs = [
    { id: "camps", label: "Camp-Anmeldungen", icon: Tent, implemented: true },
    { id: "camp-management", label: "Camp-Verwaltung", icon: Settings, implemented: true },
    { id: "akademie", label: "Akademie-Anmeldungen", icon: GraduationCap, implemented: false },
    // Future tabs can be added here:
    // { id: "selection", label: "Selection Teams", icon: Users, implemented: false },
  ];

  return (
    <div className="space-y-4">
      {/* Sub-Tab Navigation */}
      <div className="flex gap-2 flex-wrap border-b border-white/10 pb-4">
        {subTabs.map((tab) => (
          <Button
            key={tab.id}
            onClick={() => setActiveSubTab(tab.id)}
            variant={activeSubTab === tab.id ? "default" : "ghost"}
            size="sm"
            className={`${
              activeSubTab === tab.id
                ? "bg-[#c1ff72]/20 text-[#c1ff72] border border-[#c1ff72]/30"
                : "text-white/70 hover:text-white hover:bg-white/5"
            } rounded-lg`}
            data-testid={`registrations-subtab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4 mr-2" />
            {tab.label}
            {!tab.implemented && (
              <span className="ml-2 text-[10px] bg-white/10 px-1.5 py-0.5 rounded">Bald</span>
            )}
          </Button>
        ))}
      </div>

      {/* Sub-Tab Content */}
      <div className="min-h-[400px]">
        {activeSubTab === "camps" && (
          <CampRegistrationsTab token={token} />
        )}

        {activeSubTab === "camp-management" && (
          <CampManagementTab token={token} />
        )}

        {activeSubTab === "akademie" && (
          <AkademiePlaceholder />
        )}
      </div>
    </div>
  );
}

/**
 * Placeholder component for Akademie-Anmeldungen
 * This will be fully implemented in a future update
 */
function AkademiePlaceholder() {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-16 h-16 bg-[#c1ff72]/10 rounded-full flex items-center justify-center mb-6">
        <GraduationCap className="w-8 h-8 text-[#c1ff72]" />
      </div>
      <h3 className="font-['Bebas_Neue'] text-2xl text-white mb-2">
        AKADEMIE-ANMELDUNGEN
      </h3>
      <p className="text-[#A1A1AA] max-w-md mb-6">
        Die Verwaltung der Akademie-Anmeldungen wird in Kürze hier verfügbar sein.
        Aktuell werden Akademie-Registrierungen bereits in der Datenbank gespeichert.
      </p>
      <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-4 text-left max-w-sm">
        <p className="text-white/70 text-sm mb-2">Geplante Funktionen:</p>
        <ul className="text-white/50 text-sm space-y-1">
          <li>• Übersicht aller Akademie-Anmeldungen</li>
          <li>• Vertragstypen filtern (Jahres-, Halbjahres-, Monatsvertrag)</li>
          <li>• Zahlungsstatus verwalten</li>
          <li>• Excel-Export</li>
          <li>• Spieler-Profile</li>
        </ul>
      </div>
    </div>
  );
}
